/**
 * WAV encoding and decoding.
 *
 * WAV is the DAW's lingua franca: it is what recordings are stored as, what
 * stem and mixdown export produces, and the one format every browser can decode
 * without a codec. The encoder writes 16- or 24-bit PCM (or 32-bit float),
 * because those are what other audio software expects to receive.
 */

export type WavBitDepth = 16 | 24 | 32;

export interface WavEncodeOptions {
  bitDepth?: WavBitDepth;
  /** 32-bit output is written as IEEE float unless this is set. */
  float?: boolean;
}

export function encodeWav(
  channels: readonly Float32Array[],
  sampleRate: number,
  options: WavEncodeOptions = {},
): Uint8Array {
  const bitDepth = options.bitDepth ?? 24;
  const isFloat = bitDepth === 32 && options.float !== false;
  const channelCount = Math.max(1, channels.length);
  const frameCount = channels[0]?.length ?? 0;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = channelCount * bytesPerSample;
  const dataBytes = frameCount * blockAlign;

  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, isFloat ? 3 : 1, true); // 1 = PCM, 3 = IEEE float
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let frame = 0; frame < frameCount; frame++) {
    for (let c = 0; c < channelCount; c++) {
      const sample = channels[c]?.[frame] ?? 0;
      if (isFloat) {
        view.setFloat32(offset, sample, true);
        offset += 4;
      } else if (bitDepth === 16) {
        view.setInt16(offset, toPcm(sample, 32767), true);
        offset += 2;
      } else {
        const value = toPcm(sample, 8388607);
        view.setUint8(offset, value & 0xff);
        view.setUint8(offset + 1, (value >> 8) & 0xff);
        view.setUint8(offset + 2, (value >> 16) & 0xff);
        offset += 3;
      }
    }
  }

  return new Uint8Array(buffer);
}

/**
 * Clamps and converts a float sample. Clamping before scaling is what stops a
 * sample at 1.0000001 from wrapping to full-scale negative — an audible and
 * very unpleasant click.
 */
function toPcm(sample: number, peak: number): number {
  const clamped = sample < -1 ? -1 : sample > 1 ? 1 : sample;
  return Math.max(-peak - 1, Math.min(peak, Math.round(clamped * peak)));
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}

export interface DecodedWav {
  channels: Float32Array[];
  sampleRate: number;
}

/**
 * A minimal WAV reader, used by tests and by the offline renderer's
 * verification path. Production import goes through `decodeAudioData`, which
 * handles every format the browser supports; this exists so audio can be read
 * back without a browser present.
 */
export function decodeWav(data: Uint8Array): DecodedWav {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (readAscii(view, 0, 4) !== 'RIFF' || readAscii(view, 8, 4) !== 'WAVE') {
    throw new Error('Not a WAV file.');
  }

  let offset = 12;
  let format = 1;
  let channelCount = 2;
  let sampleRate = 44100;
  let bitDepth = 16;
  let dataStart = -1;
  let dataLength = 0;

  while (offset + 8 <= view.byteLength) {
    const id = readAscii(view, offset, 4);
    const size = view.getUint32(offset + 4, true);
    if (id === 'fmt ') {
      format = view.getUint16(offset + 8, true);
      channelCount = view.getUint16(offset + 10, true);
      sampleRate = view.getUint32(offset + 12, true);
      bitDepth = view.getUint16(offset + 22, true);
    } else if (id === 'data') {
      dataStart = offset + 8;
      dataLength = size;
    }
    offset += 8 + size + (size % 2);
  }

  if (dataStart < 0) throw new Error('WAV file has no data chunk.');

  const bytesPerSample = bitDepth / 8;
  const frameCount = Math.floor(dataLength / (bytesPerSample * channelCount));
  const channels = Array.from({ length: channelCount }, () => new Float32Array(frameCount));

  let p = dataStart;
  for (let frame = 0; frame < frameCount; frame++) {
    for (let c = 0; c < channelCount; c++) {
      if (format === 3) {
        channels[c][frame] = view.getFloat32(p, true);
      } else if (bitDepth === 16) {
        channels[c][frame] = view.getInt16(p, true) / 32768;
      } else if (bitDepth === 24) {
        const lo = view.getUint8(p);
        const mid = view.getUint8(p + 1);
        const hi = view.getInt8(p + 2);
        channels[c][frame] = ((hi << 16) | (mid << 8) | lo) / 8388608;
      } else if (bitDepth === 32) {
        channels[c][frame] = view.getInt32(p, true) / 2147483648;
      }
      p += bytesPerSample;
    }
  }

  return { channels, sampleRate };
}

function readAscii(view: DataView, offset: number, length: number): string {
  let out = '';
  for (let i = 0; i < length; i++) out += String.fromCharCode(view.getUint8(offset + i));
  return out;
}
